import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { ICollectionOption } from "../../models/CollectionOption";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useCollectionOptions } from "../../data/useCollectionOptions";
import useSingleCollectionOption from "../../data/useSingleCollectionOption";
import IconColorPickerModal from "./IconColorPickerModal";
import { DEFAULT_COLOR, DEFAULT_ICON, resolveIcon } from "./collectionOptionConfig";

type FormValues = {
  name: string;
  description: string;
  isActive: boolean;
  price: number;
};

export default function AddEditCollectionOption({
  collectionOption,
  setOpen,
}: {
  collectionOption?: ICollectionOption;
  setOpen: (open: boolean) => void;
}) {
  const { deleteHandlers, saveHandlers } = useDefaultCRUDHandlers("Collection Option");
  const { create } = useCollectionOptions();
  const { update, remove } = useSingleCollectionOption(collectionOption?.id ?? "");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isNegative, setIsNegative] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string>(collectionOption?.icon || DEFAULT_ICON);
  const [selectedColor, setSelectedColor] = useState<string>(collectionOption?.color || DEFAULT_COLOR);
  const { register, handleSubmit, reset } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    const absPrice = Math.abs(parseFloat(data.price as any) || 0);
    const updated: ICollectionOption = {
      ...collectionOption,
      ...data,
      price: isNegative ? -absPrice : absPrice,
      icon: selectedIcon,
      color: selectedColor,
    } as ICollectionOption;

    if (collectionOption !== undefined) {
      update.mutate(updated, {
        onSuccess: () => { saveHandlers.onSuccess(); setOpen(false); reset({}); },
        onError: saveHandlers.onError,
      });
    } else {
      create.mutate(updated, {
        onSuccess: () => { saveHandlers.onSuccess(); setOpen(false); reset({}); },
        onError: saveHandlers.onError,
      });
    }
  };

  const handleDelete = () => {
    remove.mutate(collectionOption?.id!, {
      onSuccess: () => { deleteHandlers.onSuccess(); setOpen(false); reset({}); },
      onError: deleteHandlers.onError,
    });
  };

  useEffect(() => {
    if (collectionOption === undefined) return;

    const storedPrice = collectionOption.price ?? 0;
    setIsNegative(storedPrice < 0);
    setSelectedIcon(collectionOption.icon || DEFAULT_ICON);
    setSelectedColor(collectionOption.color || DEFAULT_COLOR);

    reset({
      name: collectionOption.name ?? "",
      description: collectionOption.description ?? "",
      isActive: collectionOption.isActive ?? true,
      price: Math.abs(storedPrice),
    });
  }, [collectionOption]);

  const IconPreview = resolveIcon(selectedIcon);

  return (
    <>
      <h1 className="text-xl">{`${collectionOption ? "Edit" : "Add"} a Collection Option`}</h1>
      <small className="text-gray-500">
        <i>{collectionOption ? "Edit the collection option details" : "Add a new collection option"}</i>
      </small>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            label="Name"
            placeholder="e.g. Standard Collection"
            registerName="name"
            register={register}
          />
          <div className="flex items-center gap-3 mt-6">
            <input
              id="isActive"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
              {...register("isActive")}
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Active
            </label>
          </div>
          <div className="col-span-1 sm:col-span-2">
            <TextInput
              label="Description"
              placeholder="Describe this collection option"
              richText={true}
              registerName="description"
              register={register}
            />
          </div>

          {/* Price field with +/- toggle */}
          <div className="col-span-1 sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price Adjustment
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsNegative((n) => !n)}
                className={`flex-shrink-0 w-10 h-10 rounded-md border text-lg font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  isNegative
                    ? "bg-red-100 border-red-400 text-red-700 hover:bg-red-200"
                    : "bg-green-100 border-green-400 text-green-700 hover:bg-green-200"
                }`}
              >
                {isNegative ? "−" : "+"}
              </button>
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  {...register("price")}
                />
              </div>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              <span className="font-medium text-green-700">Positive values (+)</span> will be added to the quote price.{" "}
              <span className="font-medium text-red-700">Negative values (−)</span> will be deducted from the quote price.
            </p>
          </div>

          {/* Icon & Colour picker */}
          <div className="col-span-1 sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Icon &amp; Colour
            </label>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className={`inline-flex items-center gap-3 rounded-lg px-4 py-2.5 border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors ${selectedColor}`}
            >
              <IconPreview className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">Change icon &amp; colour</span>
            </button>
          </div>
        </div>

        <ButtonInput
          isSubmit={true}
          label={collectionOption ? "Update Collection Option" : "Create Collection Option"}
          classes="float-right"
        />

        {collectionOption && (
          <>
            <ButtonInput
              isSubmit={false}
              label="Delete Collection Option"
              onClick={() => setDeleteOpen(true)}
              classes="bg-red-600 hover:bg-red-700"
            />
            <ConfirmModal
              open={deleteOpen}
              setOpen={setDeleteOpen}
              title="Delete Collection Option"
              message="Are you sure you want to delete this collection option?"
              confirmButtonText="Delete"
              onConfirm={handleDelete}
              isLoading={remove.isLoading}
            />
          </>
        )}
      </form>

      <IconColorPickerModal
        open={pickerOpen}
        setOpen={setPickerOpen}
        selectedIcon={selectedIcon}
        selectedColor={selectedColor}
        onSelect={(icon, color) => { setSelectedIcon(icon); setSelectedColor(color); }}
      />
    </>
  );
}
